import { useTranslation } from 'react-i18next';
import type { BudgetFiscalYearStatusResponse } from '../../../types/api.js';

interface BudgetFiscalYearWarningProps {
  /** FY 切替状況 API レスポンス */
  status: BudgetFiscalYearStatusResponse | undefined;
}

/**
 * fibo予算スプレッドシートの FY 切替警告バナー。
 *  - severity='warning': 黄色。次FY 開始まで 14 日以下なのに次FYシートが未作成。
 *  - severity='critical': 赤。新FY 突入後も現FYシートが未作成（KPI 目標線が古い）。
 *  - severity='none' または status 未取得: 表示なし。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business fibo予算スプレッドシートのFY切替忘れを 14 日前から警告し、新FY突入後も未作成なら赤critical表示。シート名は SS から自動解決されるため、ここで警告するのは「SS に新FYシートを追加してください」という運用作業のリマインドのみ
 * @graph-connects none
 */
export function BudgetFiscalYearWarning({ status }: BudgetFiscalYearWarningProps) {
  const { t } = useTranslation();

  if (!status || status.severity === 'none') return null;

  const expectedSheet = `fibo予算(FY${String(status.nextFiscalYear)})`;
  const currentSheet = `fibo予算(FY${String(status.currentFiscalYear)})`;

  // severity ごとに色と文言を切替
  const isCritical = status.severity === 'critical';
  const containerClass = isCritical
    ? 'border-2 border-[var(--color-error)] bg-[var(--color-error)]/10 text-[var(--color-error)]'
    : 'border-2 border-[var(--color-warning)] bg-[var(--color-warning)]/10 text-[var(--color-warning)]';

  const title = isCritical
    ? t('kpi.budgetFiscalYearWarning.criticalTitle', { fy: status.currentFiscalYear })
    : t('kpi.budgetFiscalYearWarning.warningTitle', { fy: status.nextFiscalYear });

  const body = isCritical
    ? t('kpi.budgetFiscalYearWarning.criticalBody', {
        sheet: currentSheet,
        startDate: status.nextFiscalYearStartDate,
      })
    : t('kpi.budgetFiscalYearWarning.warningBody', {
        sheet: expectedSheet,
        startDate: status.nextFiscalYearStartDate,
        days: status.daysUntilNextFiscalYearStart,
      });

  const targetSheet = isCritical ? currentSheet : expectedSheet;

  return (
    <div
      role={isCritical ? 'alert' : 'status'}
      aria-live={isCritical ? 'assertive' : 'polite'}
      className={`mt-6 rounded-lg px-4 py-3 text-sm font-bold ${containerClass}`}
    >
      <div className="text-base mb-1">
        {isCritical ? '🚨 ' : '⚠️ '}
        {title}
      </div>
      <div className="font-normal whitespace-pre-line">{body}</div>
      <div className="font-normal mt-2 text-xs opacity-80">
        {t('kpi.budgetFiscalYearWarning.hint', { sheet: targetSheet })}
      </div>
    </div>
  );
}
